import { FocusKeyManager, FocusableOption } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    DestroyRef,
    ElementRef,
    OnDestroy,
    QueryList,
    ViewEncapsulation,
    booleanAttribute,
    computed,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { HOST_TAG_NAME } from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Observable, merge } from 'rxjs';
import { startWith, switchMap, tap } from 'rxjs/operators';

import { LIST_ROLES } from './list-roles';

export const DS_LISTITEM_SIZE_ARRAY = ['medium', 'medium-condensed', 'large', 'large-condensed'] as const;
export type DsListItemSize = (typeof DS_LISTITEM_SIZE_ARRAY)[number];
export const DS_LIST_GROUP_ROLE_ARRAY = Object.keys(LIST_ROLES) as Array<keyof typeof LIST_ROLES>;
export type DsListGroupRole = keyof typeof LIST_ROLES;

@Component({
    selector: 'ds-list-item, a[ds-list-item]',
    imports: [CommonModule],
    template: `
        <ng-content select="[slot=start]" />
        @if (showCenterSlot()) {
            <div class="ds-list-tile-text">
                <span class="ds-list-tile-title" [class.ds-list-title-strong]="boldTitle()">
                    {{ title() }}
                </span>
                @if (subtitle()) {
                    <span class="ds-list-tile-sub-title">{{ subtitle() }}</span>
                }
                @if (subtext()) {
                    <span class="ds-list-tile-sub-text">{{ subtext() }}</span>
                }
            </div>
        }
        <div class="ds-list-end-slot">
            <ng-content select="[slot=end]" />
        </div>
    `,
    styleUrl: './list-item.component.scss',
    host: {
        'class': 'ds-list-item',
        '[class.ds-list-item-inverse]': 'inverse()',
        '[class]': '"ds-list-item-" + size()',
        '[attr.role]': '_role()',
        '[class.ds-list-item-interactive]': 'interactive()',
        '[class.ds-list-item-selected]': 'selected()',
        '[attr.aria-selected]': 'selectable() ? selected() : null',
        '[attr.tabindex]': '(interactive() && listGroup) ? tabIndex() : null',
        '(click)': 'handleClick()',
        '(focus)': 'handleFocus()',
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class DsListItem implements FocusableOption {
    title = input.required<string>();
    subtitle = input<string>('');
    subtext = input<string>('');
    selected = model<boolean>(false);
    inverse = input(false, { transform: booleanAttribute });
    boldTitle = input(true, { transform: booleanAttribute });
    showCenterSlot = input(true, { transform: booleanAttribute });
    size = input<DsListItemSize>('large');
    tagName: string | null = inject(HOST_TAG_NAME, { optional: true });

    readonly itemClick = output<DsListItem>();
    readonly itemFocus = output<DsListItem>();

    itemClick$ = outputToObservable<DsListItem>(this.itemClick);
    itemFocus$ = outputToObservable<DsListItem>(this.itemFocus);

    readonly elementRef = inject(ElementRef);
    readonly listGroup = inject(DsListGroup, { optional: true });

    public tabIndex = signal(-1);

    readonly _role = computed(() => {
        const roleType = this.listGroup?.roleType();
        return roleType && LIST_ROLES[roleType] ? LIST_ROLES[roleType].child : null;
    });

    isAnchor() {
        return this.tagName === 'a';
    }

    selectable() {
        if (!this.listGroup) return false;

        const role = this.listGroup.roleType();
        return role ? LIST_ROLES[role].supportsSelection : false;
    }

    interactive() {
        if (!this.listGroup) return true;

        const role = this.listGroup.roleType();
        return role ? LIST_ROLES[role].supportsInteraction || this.isAnchor() : false;
    }

    focus(): void {
        this.elementRef.nativeElement.focus();
    }

    setTabIndex(index: number): void {
        this.tabIndex.set(index);
    }

    handleClick(): void {
        if (this.isAnchor()) {
            return;
        }

        this.itemClick.emit(this);
    }

    handleFocus(): void {
        if (this.interactive()) {
            this.itemFocus.emit(this);
        }
    }
}

@Component({
    selector: 'ds-list-group',
    standalone: true,
    template: `<ng-content />`,
    host: {
        '[attr.role]': '_role()',
        '[attr.aria-multiselectable]': 'ariaMultiSelectable()',
        '(keydown)': 'handleKeydown($event)',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsListGroup implements AfterViewInit, OnDestroy {
    public keyManager!: FocusKeyManager<DsListItem>;
    private destroyRef = inject(DestroyRef);

    readonly selectionMode = input<'single' | 'multiselect'>('single');
    readonly roleType = input<DsListGroupRole | null>(null);
    selectedItems = signal<DsListItem[]>([]);

    @ContentChildren(DsListItem, { descendants: true })
    items!: QueryList<DsListItem>;

    readonly supportsSelection = computed(() => {
        const role = this.roleType();
        return role ? LIST_ROLES[role].supportsSelection : false;
    });

    readonly supportsInteraction = computed(() => {
        const role = this.roleType();
        const hasAnchorItems = this.items?.some((item) => item.isAnchor());
        return role ? LIST_ROLES[role].supportsInteraction || hasAnchorItems : false;
    });

    ngAfterViewInit() {
        this.items.changes
            .pipe(
                startWith(this.items),
                tap(() => this.setupKeyManager()),
                switchMap(() => this.setupEventListeners()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private setupClickListeners(): Observable<unknown> {
        return merge(
            ...this.items.map((item) =>
                item.itemClick$.pipe(
                    tap((clickedItem) => {
                        if (this.supportsSelection()) {
                            this.selectItem(clickedItem);
                        }
                        if (this.supportsInteraction()) {
                            this.keyManager.setActiveItem(clickedItem);
                        }
                    }),
                ),
            ),
        );
    }

    private setupFocusListeners(): Observable<unknown> {
        return merge(
            ...this.items.map((item) =>
                item.itemFocus$.pipe(
                    tap((focusedItem) => {
                        if (this.supportsInteraction()) {
                            this.keyManager.setActiveItem(focusedItem);
                        }
                    }),
                ),
            ),
        );
    }

    private setupEventListeners(): Observable<unknown> {
        return merge(this.setupClickListeners(), this.setupFocusListeners());
    }

    private setupKeyManager(): void {
        const interactiveItems = this.items.filter((item) => item.interactive());

        if (this.keyManager) {
            this.keyManager.destroy();
        }

        this.keyManager = new FocusKeyManager(interactiveItems).withVerticalOrientation().withWrap();

        if (interactiveItems.length > 0) {
            interactiveItems.forEach((item) => item.setTabIndex(-1));
            interactiveItems[0]?.setTabIndex(0);

            this.keyManager.change.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeIndex: number) => {
                if (activeIndex !== -1) {
                    interactiveItems.forEach((item, i) => {
                        item.setTabIndex(i === activeIndex ? 0 : -1);
                    });
                }
            });
        }
    }

    handleKeydown(event: KeyboardEvent) {
        if (this.supportsInteraction()) {
            this.keyManager.onKeydown(event);

            if (['Enter', 'Space'].includes(event.code)) {
                const activeItem = this.keyManager.activeItem;
                if (this.supportsSelection() && activeItem) {
                    this.selectItem(activeItem);
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        }
    }

    selectItem(item: DsListItem) {
        if (!this.supportsSelection()) return;

        const wasSelected = this.selectedItems().includes(item);

        if (this.selectionMode() === 'single') {
            const prevSelected = this.selectedItems()[0];
            if (prevSelected && prevSelected !== item) {
                prevSelected.selected.set(false);
            }
            this.selectedItems.set(wasSelected ? [] : [item]);
        } else {
            const items = wasSelected ? this.selectedItems().filter((i) => i !== item) : [...this.selectedItems(), item];
            this.selectedItems.set(items);
        }

        item.selected.set(!wasSelected);
    }

    readonly ariaMultiSelectable = computed(() => {
        if (!this.supportsSelection() || this.items.length === 0) {
            return null;
        }

        return this.selectionMode() === 'multiselect' ? 'true' : 'false';
    });

    readonly _role = computed(() => {
        const roleType = this.roleType();
        return roleType && LIST_ROLES[roleType] ? LIST_ROLES[roleType].parent : null;
    });

    ngOnDestroy() {
        this.keyManager?.destroy();
    }
}
